import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { api, getToken, setToken, UnauthorizedError } from './lib/api';
import { cacheGet } from './lib/offline';

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState(null);
  const [booting, setBooting] = useState(true);
  const [toastMsg, setToastMsg] = useState('');
  const toastTimer = useRef();

  const toast = useCallback((msg) => {
    setToastMsg(String(msg || ''));
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 2600);
  }, []);

  const signOut = useCallback(() => {
    setToken('');
    setUser(null);
    setSettings(null);
  }, []);

  /** Shared tail of every sign-in path, whatever proved the identity. */
  const adoptSession = useCallback((r) => {
    setToken(r.token);
    setUser(r.user);
    // Settings drive the letterhead on every page; fetch once per session.
    api.get('/settings').then(setSettings).catch(() => {});
    return r.user;
  }, []);

  const signIn = useCallback(async (email, password) => {
    const r = await api.post('/auth/login', { email: email.trim().toLowerCase(), password });
    return adoptSession(r);
  }, [adoptSession]);

  const signInWithGoogle = useCallback(async (credential) => {
    const r = await api.post('/auth/google', { credential });
    return adoptSession(r);
  }, [adoptSession]);

  // Restore the session on first load. A stored token may have expired, in
  // which case /auth/me 401s and we land on the login screen.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getToken()) { setBooting(false); return; }
      try {
        const [me, s] = await Promise.all([
          api.get('/auth/me'),
          api.get('/settings').catch(() => null),
        ]);
        if (!cancelled) { setUser(me); setSettings(s); }
      } catch (e) {
        if (cancelled) return;

        // Only an actual auth failure may end the session. This used to clear
        // the token on *any* error, so a 429, a 500 or a dropped connection
        // during boot silently signed the user out — which for a rep opening
        // the app on a roof with one bar meant being thrown back to the login
        // screen for no reason they could see.
        if (e instanceof UnauthorizedError) {
          setToken('');
        } else {
          // Network or server trouble. Keep the session and fall back to the
          // cached identity so the app still opens offline.
          const cachedMe = await cacheGet('/auth/me').catch(() => null);
          if (cachedMe?.data) {
            setUser(cachedMe.data);
            const cachedSettings = await cacheGet('/settings').catch(() => null);
            if (cachedSettings?.data) setSettings(cachedSettings.data);
          }
        }
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * Wraps an async action with the error handling every screen needs: a 401
   * drops the session, anything else surfaces as a toast rather than an
   * unhandled rejection the user never sees.
   */
  const run = useCallback(async (fn, { onError } = {}) => {
    try {
      return await fn();
    } catch (e) {
      if (e instanceof UnauthorizedError) { signOut(); return undefined; }
      (onError || toast)(e.message || 'Something went wrong');
      return undefined;
    }
  }, [signOut, toast]);

  return (
    <Ctx.Provider value={{
      user, setUser, settings, setSettings, booting,
      signIn, signInWithGoogle, signOut, toast, toastMsg, run,
    }}>
      {children}
    </Ctx.Provider>
  );
}
